import { inject, injectable } from 'inversify';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { iocContainer, TYPES } from '@/engine/ioc';
import type { IWasmFeatures, IWasmModulesRegistry } from '../WasmModules/Interfaces';
import type { CaseDataProvider } from './CaseDataProvider';
import type { ViewsService } from './Views.service';
import type { ImagesService } from './modules/images/Images.service';
import { TYPES as ImagesTYPES } from './modules/images/iocTypes';
import { TYPES as ReportingTYPES } from './modules/reporting';
import type { CasePersistenceHub } from './CasePersistance/CasePersistenceHub';
import { CaseState } from './models/CaseState';
import type { IFrameController } from './IFrame.controller';
import type { SavingStateController } from './SavingState.controller';
import type { EnvInfo, ICineloopInfo } from './interfaces';
import type { SurgeryRegistry } from './registries/SurgeryRegistry.ts';
import type { ImporterFactory } from '../Serialization/Importer.factory';
import { Average } from '../utils/Average';
import type { CasePhaseData } from './CasePhaseData/CasePhaseData.ts';
import { type UIConfigResolver } from '@/engine/app/modules/ui';
import { throttle } from 'underscore';
import { type CreateReportUseCase } from './modules/reporting/CreateReportUseCase';
import { ReportFormat } from './modules/reporting/ReportFormat';
import type { CasePersistenceRegistrar } from './CasePersistance/CasePersistenceRegistrar.ts';
import FileSaver from 'file-saver';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { SynchronizeTabsController } from '@/components/viewsSynchronization/SynchronizeTabsController.ts';
import { config } from '@/config/app.config';

function _toString(feat: IWasmFeatures): string {
  const supported: Array<string> = [];
  const unsupported: Array<string> = [];
  (feat.bigInt ? supported : unsupported).push('BigInt integration');
  (feat.bulkMemory ? supported : unsupported).push('Bulk memory operations');
  (feat.exceptions ? supported : unsupported).push('Exception handling');
  (feat.memory64 ? supported : unsupported).push('Memory64 (optional)');
  (feat.multiValue ? supported : unsupported).push('Multi-value');
  (feat.mutableGlobals ? supported : unsupported).push('Importable/Exportable mutable globals');
  (feat.referenceTypes ? supported : unsupported).push('Reference Types');
  (feat.saturatedFloatToInt ? supported : unsupported).push('Non-trapping float-to-int conversions');
  (feat.signExtensions ? supported : unsupported).push('Sign-extension operators');
  (feat.simd ? supported : unsupported).push('Fixed-Width SIMD');
  (feat.tailCall ? supported : unsupported).push('Tail call (optional)');
  (feat.threads ? supported : unsupported).push('Threads (optional)');
  let ret = 'Not Supported:';
  unsupported.forEach((feature) => (ret += '\n  ' + feature));
  ret += '\nSupported:';
  supported.forEach((feature) => (ret += '\n  ' + feature));
  return ret;
}

@injectable()
export class AppController implements ICineloopInfo {
  private readonly _viewsService: ViewsService;

  private readonly _imagesService: ImagesService;

  private readonly _wasmModulesRegistry: IWasmModulesRegistry;

  private readonly _casePersistenceHub: CasePersistenceHub;

  private readonly _caseDataProvider: CaseDataProvider;

  private readonly _iframeController: IFrameController;

  private readonly _surgeryRegistry: SurgeryRegistry;

  private readonly _importerFactory: ImporterFactory;

  private readonly _casePhaseData: CasePhaseData;

  private readonly _uiConfigResolver: UIConfigResolver;

  private _alreadyInitialized = false;

  @observable private _surgeryType: string | undefined;

  /** [chebotarev] BEGIN temp code for cineloop validation */
  private _cineloopTimeout?: NodeJS.Timeout;

  private _cineloopTargetBpm = 60;

  @observable private _cineloopCurrentBpm?: number;

  @observable private _isCineloopActive = false;

  private _cineloopAverage = new Average(Math.max(Math.round(this._cineloopTargetBpm / 5), 2));
  /** [chebotarev] END temp code for cineloop validation */

  @observable private readonly _envInfo: EnvInfo = {
    productInfoBuildUIVersion: config.productInfo.uiBuildVersion,
    productInfoBuildAPIVersion: config.productInfo.apiBuildVersion
  };

  private readonly _savingStateController: SavingStateController;

  private _caseState?: CaseState;

  @observable private _isLoadingOverlayActive = true;

  @observable private _hasAppInitializationCompleted = false;

  @observable private _progressOverlayMessage: Array<string> | undefined = undefined;

  private readonly _loggerService: ObservabilityLoggerService;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService,
    @inject(TYPES.IWasmModulesRegistry) wasmModulesRegistry: IWasmModulesRegistry,
    @inject(TYPES.CasePersistenceRegistrar) casePersistenceRegistrar: CasePersistenceRegistrar,
    @inject(TYPES.CasePersistenceHub) casePersistenceHub: CasePersistenceHub,
    @inject(TYPES.CaseDataProvider) caseDataProvider: CaseDataProvider,
    @inject(TYPES.IFrameController) iframeController: IFrameController,
    @inject(TYPES.SavingStateController) savingStateController: SavingStateController,
    @inject(TYPES.SurgeryRegistry) surgeryRegistry: SurgeryRegistry,
    @inject(TYPES.ImporterFactory) importerFactory: ImporterFactory,
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(TYPES.UIConfigResolver) uiConfigResolver: UIConfigResolver,
    @inject(CORE_TYPES.ObservabilityLoggerService) loggerService: ObservabilityLoggerService
  ) {
    casePersistenceRegistrar.init(casePersistenceHub);

    this._casePersistenceHub = casePersistenceHub;
    this._viewsService = viewsService;
    this._imagesService = imagesService;
    this._wasmModulesRegistry = wasmModulesRegistry;
    this._caseDataProvider = caseDataProvider;
    this._iframeController = iframeController;
    this._savingStateController = savingStateController;
    this._surgeryRegistry = surgeryRegistry;
    this._importerFactory = importerFactory;
    this._casePhaseData = casePhaseData;
    this._uiConfigResolver = uiConfigResolver;
    this._loggerService = loggerService;

    makeObservable(this);

    (window as unknown as { app: AppController }).app = this;
  }

  @computed
  public get isLoadingOverlayActive(): boolean {
    return this._isLoadingOverlayActive;
  }

  @computed
  public get hasAppInitializationCompleted(): boolean {
    return this._hasAppInitializationCompleted;
  }

  @computed
  public get progressOverlayMessage(): Array<string> | undefined {
    return this._progressOverlayMessage;
  }

  public async init(caseId: string, packageId: string): Promise<void> {
    // in development mode this method can be called multiple times
    if (this._alreadyInitialized) {
      return;
    }
    this._alreadyInitialized = true;

    const wasmFeatures = await this._wasmModulesRegistry.detectWasmFeatures();
    if (
      !wasmFeatures.bigInt ||
      !wasmFeatures.bulkMemory ||
      !wasmFeatures.exceptions ||
      !wasmFeatures.multiValue ||
      !wasmFeatures.mutableGlobals ||
      !wasmFeatures.referenceTypes ||
      !wasmFeatures.saturatedFloatToInt ||
      !wasmFeatures.signExtensions ||
      !wasmFeatures.simd
    ) {
      alert(
        'WARNING! One or more required WASM features are not supported by your browser. Application may work incorrectly.\n\n' +
          _toString(wasmFeatures)
      );
    }

    await this._wasmModulesRegistry.loadAllModules();
    await this._caseDataProvider.init(caseId, packageId);

    await this._loadSurgeries();

    const caseInfo = await this._caseDataProvider.getCaseInfo();
    this._loggerService.setGlobalContext(caseInfo);

    const surgeryType = this._surgeryRegistry.resolveSurgeryType(caseInfo);

    runInAction(() => {
      this._surgeryType = surgeryType;
    });

    await this._surgeryRegistry.init(surgeryType);

    iocContainer.load(this._surgeryRegistry.getIoCModule());

    // Creating views according to the config
    await this._viewsService.loadViewsConfig(this._surgeryRegistry.getCurrentSurgeryViewsConfig());

    this._surgeryRegistry.registerPresentationCreators();

    this._uiConfigResolver.setSurgerySpecificConfig(this._surgeryRegistry.getCurrentSurgeryUiConfig());

    // This must be done before Case Opening!
    this._surgeryRegistry.getCurrentSurgeryCasePersistanceExtension()?.registerHandlers(this._casePersistenceHub);

    // Waiting for case data opening and loading actual state of PlanningAlgo, images and items of views
    await this._casePersistenceHub.openCase(async (): Promise<void> => {
      if (!this._surgeryType) {
        throw new Error('Surgery type should be defined');
      }
      const importer = this._importerFactory.create(this._surgeryType);
      await importer.processImport(this._surgeryRegistry.getCurrentSurgeryMCSDImporterConfig());
    });

    // Creating images according to the config
    await this._imagesService.loadImagesConfig(this._surgeryRegistry.getCurrentSurgerySliceOrientationsConfig());

    //Filtering views according to availability info, received from Planning Algo
    this._viewsService.checkViewsForAvailabilityAndFilter();

    //Setting current view
    if (!this._viewsService.currentView) {
      this._viewsService.setCurrentView(this._viewsService.views[0]);
    }

    this._caseState = new CaseState(caseInfo);
    document.title = caseInfo.caseCode;
    this._iframeController.initMipIntegration(caseInfo.sid);

    runInAction(() => {
      this._hasAppInitializationCompleted = true;
    });

    await this._surgeryRegistry.onModuleLoadedAsync();

    (window as unknown as { createReport: CreateReportUseCase }).createReport = iocContainer.get<CreateReportUseCase>(
      ReportingTYPES.CreateReportUseCase
    );

    this._savingStateController.init(
      this._caseState.userPermissions.canSaveCase,
      this._surgeryRegistry.getCurrentSurgerySavingStateUpdateSubscriptions()
    );
    this._hideLoadingOverlay();
  }

  private _hideLoadingOverlay(): void {
    runInAction(() => {
      this._isLoadingOverlayActive = false;
    });
  }

  private _showProgressOverlay(...multilineMessage: Array<string>): void {
    runInAction(() => {
      this._progressOverlayMessage = multilineMessage;
    });
  }

  private _hideProgressOverlay(): void {
    runInAction(() => {
      this._progressOverlayMessage = undefined;
    });
  }

  private async _tempPhaseSwitch<T>(action: () => Promise<T>): Promise<T> {
    const idsToRestore = new Map<string, number>();
    try {
      for (const kind of this._casePhaseData.getAvailableKinds()) {
        const initialPhaseId = this._casePhaseData.getInitialPhaseIdx(kind);
        const activePhaseId = this._casePhaseData.getActivePhaseIdx(kind);
        if (activePhaseId != initialPhaseId) {
          // memoise phase index before switching
          idsToRestore.set(kind, activePhaseId);
          // switch
          await this._casePhaseData.switchAsync(kind, initialPhaseId);
        }
      }
      // do something
      return await action();
    } finally {
      for (const [kind, idx] of idsToRestore) {
        // switch back to memoised phase index
        await this._casePhaseData.switchAsync(kind, idx);
      }
    }
  }

  public async saveCase(): Promise<void> {
    try {
      this._showProgressOverlay('Case saving is in progress.', 'Please wait...');
      await this._tempPhaseSwitch(() => this._casePersistenceHub.saveCase());
      this._savingStateController.setChangedState(false);
    } finally {
      this._hideProgressOverlay();
    }
  }

  public async approveCase(): Promise<void> {
    try {
      this._showProgressOverlay(
        'Case approval is in progress.',
        'You will be automatically redirected to the Case Management System when done.'
      );
      const uri = await this._tempPhaseSwitch(async () => {
        await this._casePhaseData.approveTuneValuesDefaults();
        return await this._casePersistenceHub.approveCase({
          descriptor: 'PlanningReport',
          fileName: 'Report.pptx',
          blob: await this._generateReport(ReportFormat.PPTX)
        });
      });
      this._savingStateController.setChangedState(false);
      window.location.replace(uri);
    } catch (e: unknown) {
      this._hideProgressOverlay();
      throw e;
    }
  }

  public async rejectCase(): Promise<void> {
    const uri = await this._casePersistenceHub.rejectCase();
    this._savingStateController.setChangedState(false); // Set it to unchanged state
    window.location.replace(uri);
  }

  public initSaveCaseSubscriptons(): void {
    this._savingStateController.initSubscriptions();
  }

  /** [chebotarev] BEGIN temp code for cineloop validation */
  @computed
  public get cineloopCurrentBpm(): number | undefined {
    return this._cineloopCurrentBpm;
  }

  @computed
  public get isCineloopActive(): boolean {
    return this._isCineloopActive;
  }

  @computed
  public get surgeryType(): string | undefined {
    return this._surgeryType;
  }

  public setCineloopCineloopTargetBpm(bpm: number): void {
    this._cineloopTargetBpm = Math.max(Math.min(bpm, 200), 1);
    this._cineloopAverage.resize(Math.max(Math.round(this._cineloopTargetBpm / 5), 2));
  }

  public async triggerCineloop(kind: string): Promise<void> {
    const _setCineloopCurrentBpm = throttle(
      (bpm: number) => {
        runInAction(() => {
          this._cineloopCurrentBpm = bpm;
        });
      },
      1000,
      {
        leading: false, // let's not apply first update: wait for value gets stabilized just after the start
        trailing: false // let's not apply last update: will set the value explicitly to undefined at the end
      }
    );
    const ids: Array<string> = this._casePhaseData.getAvailablePhases(kind);
    if (this._cineloopTimeout) {
      clearTimeout(this._cineloopTimeout);
      this._cineloopTimeout = undefined;
      this._cineloopAverage.clear();
      await this._casePhaseData.clearPlanningAlgorithmSnapshots();
      runInAction(() => {
        this._cineloopCurrentBpm = undefined;
        this._isCineloopActive = false;
      });
    } else {
      runInAction(() => {
        this._isCineloopActive = true;
      });
      let prev = Date.now();
      const _runJob = async (): Promise<void> => {
        const before = Date.now();
        await this.changePhaseAsync(kind, undefined);
        if (this._cineloopTimeout) {
          clearTimeout(this._cineloopTimeout);
          const targetFps = Math.min((this._cineloopTargetBpm * ids.length) / 60, 200);
          const now = Date.now();
          this._cineloopTimeout = setTimeout(() => void _runJob(), Math.max(1000 / targetFps - (now - before + 7), 1));
          const average = this._cineloopAverage.update(now - prev);
          _setCineloopCurrentBpm(((1000 / average) * 60) / ids.length);
          prev = now;
        }
      };
      this._cineloopTimeout = setTimeout(() => void _runJob(), 1);
    }
  }

  private async _changePhaseAsync(kind: string, index?: number): Promise<void> {
    const guid = await this._casePhaseData.switchAsync(kind, index);
    await this._imagesService.switchImagesAsync(guid);
  }
  /** [chebotarev] END temp code for cineloop validation */

  public async changePhaseAsync(kind: string, index?: number): Promise<void> {
    await this._changePhaseAsync(kind, index);
  }

  public getCaseState(): CaseState {
    if (!this._caseState) {
      throw new Error('Case Info Store is not initialized');
    }
    return this._caseState;
  }

  public getEnvInfo(): EnvInfo {
    return this._envInfo;
  }

  private async _generateReport(reportFormat: ReportFormat): Promise<Blob> {
    const createReport = iocContainer.get<CreateReportUseCase>(ReportingTYPES.CreateReportUseCase);
    return await createReport.execute({ reportFormat });
  }

  public async generateReport(): Promise<void> {
    const reportFormat = this._surgeryRegistry.getCurrentSurgeryReportConfig() ?? ReportFormat.PPTX;
    const reportBlob = await (async (): Promise<Blob> => {
      try {
        this._showProgressOverlay('Report is being generated.', 'Please wait...');
        return await this._generateReport(reportFormat);
      } finally {
        this._hideProgressOverlay();
      }
    })();
    try {
      FileSaver.saveAs(reportBlob, `Report.${reportFormat}`);
    } catch (error: unknown) {
      console.error('Error while saving report file', error);
      throw error;
    }
  }

  private async _loadSurgeries(): Promise<void> {
    const surgeriesEntryPoints = (window as unknown as { surgeriesEntryPoints: Record<string, () => Promise<unknown>> })
      .surgeriesEntryPoints;
    const surgeries = Object.keys(surgeriesEntryPoints);
    await Promise.all(
      surgeries.map(async (surgery) => {
        const main = (await surgeriesEntryPoints[surgery]()) as {
          default: (registry: SurgeryRegistry) => Promise<void>;
        };
        if (typeof main.default === 'function') {
          return main.default(this._surgeryRegistry);
        }
      })
    );
  }

  public activateTabsSync(): void {
    if (!this._hasAppInitializationCompleted) {
      console.warn('Wait for scene loading.');
      return;
    }
    if (!this._imagesService.isReady()) {
      console.warn('Wait for images loading.');
      return;
    }
    const syncTabsController = iocContainer.get<SynchronizeTabsController>(TYPES.SynchronizeTabsController);
    syncTabsController.activate();
  }

  public deactivateTabsSync(): void {
    const syncTabsController = iocContainer.get<SynchronizeTabsController>(TYPES.SynchronizeTabsController);
    syncTabsController.deactivate();
  }
}
